'use client';
/** @jsxImportSource @emotion/react */

import React from 'react';

import type { AlertProps as MuiAlertProps } from '@mui/material/Alert';

import MuiAlert, { alertClasses } from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

import { styled } from '../styled/styled';
type ExcludedMuiProps =
  | 'closeText'
  | 'color'
  | 'component'
  | 'icon'
  /**
   * Removed the `component` prop from the `Alert` component, as it should not be modified
   * because its underlying HTML element is important for maintaining correct
   * accessibility roles (like aria-live and role="alert") and styling
   * And the default implementation in MUI is also broken, and altering it could lead to issues.
   *
   * https://serko.atlassian.net/browse/ZDS-1032
   */
  | 'iconMapping';

type AlertProps = Omit<MuiAlertProps, ExcludedMuiProps>;

export type { AlertProps };

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ sx = [], ...props }, ref) => {
    const theme = useTheme();
    return (
      <StyledAlert
        {...props}
        ref={ref}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            [theme.breakpoints.down('sm')]: {
              flexWrap: props.action ? 'wrap' : undefined,
            },
          },
        ]}
      />
    );
  }
);

Alert.displayName = 'Alert';

const StyledAlert = styled(MuiAlert)(({ action, theme }) => {
  const actionChildSelector = `& .${alertClasses.action} > *:first-of-type`;
  return {
    ...(action && {
      [actionChildSelector]: {
        whiteSpace: 'nowrap',
      },
      [theme.breakpoints.down('sm')]: {
        [`& .${alertClasses.action}`]: {
          flexBasis: '100%',
          paddingLeft: theme.spacing(4),
        },
        [`& .${alertClasses.icon}`]: {
          flex: '0 1',
        },
        [`& .${alertClasses.message}`]: {
          flex: '1 0',
        },
        [actionChildSelector]: {
          width: '100%',
        },
      },
    }),
  };
});
